/* eslint-disable no-use-before-define */
import { ConfigurationService } from '@treasury/core/config';
import { TmHttpClient, TmHttpResponseType } from '@treasury/core/http';
import { exists, getKeys, ObservationSource, SessionStorageService } from '@treasury/utils';
import { DiContainer, Injectable } from '@treasury/utils/dependency-injection';
import { EntitlementDto, getEntitlementName } from './entitlement.dto';
import { normalizeFeatureName } from './feature-flag.helpers';
import {
    EntitlementFeatureFlag,
    Feature,
    LocalFeatureFlag,
    LocalFeatureJson,
    TmFeatureFlag,
} from './feature-flag.types';

const LOCAL_FLAGS_PATH = '/feature-flags.json';
const STORAGE_PREFIX = 'TM_FF';

// TODO: consolidate this with BO feature flag service
@Injectable()
export class FeatureFlagService {
    // eslint-disable-next-line no-useless-constructor
    constructor(
        private http: TmHttpClient,
        private storage: SessionStorageService,
        private config: ConfigurationService
    ) {}

    private _features: Map<Lowercase<Feature>, TmFeatureFlag> = new Map();

    private readonly readyStream = new ObservationSource<boolean>();

    public readonly ready$ = this.readyStream.toObservable();

    public get features() {
        return Array.from(this._features.values());
    }

    /** @deprecated Use DI instead of direct import. */
    static async isEnabled(...flags: string[]) {
        const instance = await getServiceInstance();
        return instance.isEnabled(...(flags as Feature[]));
    }

    /** @deprecated Use DI instead of direct import. */
    static async getFeatureFlags() {
        const instance = await getServiceInstance();
        // eslint-disable-next-line no-use-before-define
        return instance.getEntitlements();
    }

    static async init(...args: Parameters<FeatureFlagService['init']>) {
        return (await getServiceInstance()).init(...args);
    }

    static getInstance() {
        return getServiceInstance();
    }

    /**
     * Determine if one or more features are all enabled.
     * Consults local overrides first in dev builds.
     *
     * @param flagNames  A list of feature flag names to check.
     * @returns A promise containing `true` if—and only if—all features are enabled, otherwise `false`.
     */
    public async isEnabled(...flagNames: Feature[]) {
        await this.ready$.toPromise();

        return flagNames.every(name => {
            const normalizedName = normalizeFeatureName(name);
            const feature = this._features.get(normalizedName);
            if (!feature) {
                console.warn(
                    `Attempted to query status of feature ${name}, but it was not present in the feature flag registry.`
                );
                return false;
            }

            const locallyEnabled = this.getOverride(name);
            return exists(locallyEnabled) ? locallyEnabled : feature.enabled;
        });
    }

    public setOverride(featureName: Feature, enabled: boolean) {
        this.storage.set(`${STORAGE_PREFIX}.${normalizeFeatureName(featureName)}`, enabled);
    }

    public getOverride(featureName: Feature) {
        return this.storage.get<boolean>(`${STORAGE_PREFIX}.${normalizeFeatureName(featureName)}`);
    }

    public async init(localFlagsPath = LOCAL_FLAGS_PATH) {
        if (this.ready$.completed) {
            return;
        }

        let features: TmFeatureFlag[] = [];

        try {
            features = (
                await Promise.all([
                    this.getEntitlementFeatures(),
                    this.getLocalFeatures(localFlagsPath),
                ])
            ).flat();
        } catch (e) {
            this.readyStream.setError(new Error('Could not initialize feature flag service.'));
            this.readyStream.complete();
            throw e;
        }

        features.forEach(f => {
            this._features.set(normalizeFeatureName(f.name), f);
        });

        this.readyStream.emit(true);
    }

    public reset() {
        this._features.clear();
        this.readyStream.emit(false);
    }

    private async getLocalFeatures(path: string) {
        try {
            const json = await this.http.request<LocalFeatureJson>(path, {
                method: 'GET',
                responseType: TmHttpResponseType.Json,
                // disable base URL to request non-FI-scoped asset
                withoutBase: true,
            });

            return getKeys(json).map(k => {
                const value = json[k];
                const fiId = this.config.institutionId;
                const enabled = Array.isArray(value)
                    ? // normalize values to allow for all casing permutations
                      value.map(v => v.toLowerCase()).includes(fiId.toLowerCase())
                    : !!value;

                return new LocalFeatureFlag(k, enabled);
            });
        } catch (e) {
            // fail back to empty array and warn if local feature flags are not available
            console.warn(`Could not load local feature flags from ${path}.`);
            return [];
        }
    }

    /**
     * Get entitlements and convert them to a dictionary of `Feature` objects.
     * @returns
     */
    private async getEntitlementFeatures() {
        const entitlements = await this.getEntitlements();
        return entitlements.map(e => new EntitlementFeatureFlag(e));
    }

    /**
     * Get the underlying entitlement DTOs used to inform feature flag states.
     */
    private async getEntitlements() {
        const entitlements = await this.http.request<EntitlementDto[]>(`entitlements`, {
            method: 'GET',
            maxAgeInSeconds: 50000,
        });

        return entitlements.filter(e => getEntitlementName(e).toLowerCase().includes('feature'));
    }
}

async function getServiceInstance() {
    return (await DiContainer.getInstance()).get(FeatureFlagService);
}
